import { Alert, Button } from "@mui/material";
import { isBrowserPlatformOffered } from "./editQuizHelper";
import React, { Dispatch, ForwardedRef, forwardRef, SetStateAction, useCallback, useImperativeHandle } from "react";
import SaveProfile from "./edit-quiz-modals/SaveProfileWrapper";
import styles from "./editQuiz.module.css";
import { api as defaultApi, type LiveSession, VerityApi, VerityApiContext } from "../api/fetch";
import { AssessmentInfo } from "@verity/api-client/types";
import { StepConfig } from "../core";
import type { CoreFileItem } from "../core";
import {
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { EditQuizStep, isProctoringOptionCategory } from "./proctoring-options/option-text/ProctoringOptionText";
import { VerityLogger } from "@yujacorp/verity-logger";
import i18nPackage from "../i18n/i18n";
import { I18nextProvider } from "react-i18next";
import type { i18n } from "i18next";
import ConfirmationPage from "./edit-quiz-steps/ConfirmationStep";
import ConfigurationStep from "./edit-quiz-steps/ConfigurationStep";
import LiveSessionsStep from "./edit-quiz-steps/LiveSessionStep";
import MethodStep from "./edit-quiz-steps/MethodStep";
import PlatformStep from "./edit-quiz-steps/PlatformStep";
import ThirdPartyStep from "./edit-quiz-steps/ThirdPartyStep";
import DisclaimerModal from "./edit-quiz-modals/disclaimerModal";
import NoConfigurationsEnabledModal from "./edit-quiz-modals/noConfirmationEnabledModal";
import useEditQuizNavigation from "./hooks/useEditQuizNavigation";
import useEditQuizSubmit from "./hooks/useEditQuizSubmit";
import useEditQuizInit from "./hooks/useEditQuizInit";
import { EditQuizEntryPoint, EditQuizType, ExemptedStudent, ThirdPartyPlatform } from "../types/common";
import UnsavedChangesModal from "./edit-quiz-modals/UnsavedChangesModal";
import { InitialProctoringState, UnsavedAssessmentInfo } from "./editQuizModalWrapper";
import useEditQuizState from "./hooks/state/useEditQuizState";
import ProfileStep from "./edit-quiz-steps/ProfileStep";
import useEditQuizProfile from "./hooks/useEditQuizProfile";
import EditQuizStepper from "./components/EditQuizStepper";
import {
	type EditQuizAssessmentUUids,
	getLmsScheduleInfo,
	type LiveSessionWithInvitations,
	type PendingLiveSession,
} from "./live/liveHelper";
import { ExemptionSectionProps, ExemptionSectionView } from "./accommodation/ExemptionSection";

export type EditQuizButtonState = {
	enabled?: boolean;
	hide?: boolean;
	tooltip?: React.ReactNode;
	text?: string;
	highlightText?: React.ReactNode;
	element?: HTMLButtonElement | null;
};

export type OnSaveOptionsResults = Awaited<ReturnType<VerityApi["saveProctoringAssessmentConfiguration"]>>;

export type EditQuizPageProps = {
	quizName?: string;
	courseName?: string;
	url?: string;
	api?: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	initialProctoringAssessmentConfiguration?: Omit<
		Partial<ProctoringAssessmentConfigurationV2>,
		"proctoringOptionProfile"
	>;
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	setProctoringAssessmentConfiguration: (
		options: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
	) => void;
	initialProctoringOption?: Partial<ProctoringOption>;
	proctoringOption?: Partial<ProctoringOption>;

	setProctoringOption: (options: Partial<ProctoringOption>) => void;
	currentProfileId?: number;
	setCurrentProfileId: (id: number | undefined) => void;
	existingLiveSessions: Record<string, LiveSessionWithInvitations>;
	setExistingLiveSessions: React.Dispatch<React.SetStateAction<Record<string, LiveSessionWithInvitations>>>;
	pendingLiveSessions: Record<string, PendingLiveSession>;
	setPendingLiveSessions: React.Dispatch<React.SetStateAction<Record<string, PendingLiveSession>>>;
	pendingLiveSessionIds: string[];
	setPendingLiveSessionIds: React.Dispatch<React.SetStateAction<string[]>>;
	currFakeLiveSessionId: React.MutableRefObject<number>;
	/**
	 * The schedule to open with for a caller that has one but no assessment to fetch it from. Read only
	 * where `getAssessmentLiveSessions` would otherwise have been called, so it reaches the wizard through
	 * exactly the code a quiz-backed load goes through; every caller that omits it is untouched.
	 */
	initialLiveSessions?: LiveSession[];
	assessmentUuids: React.MutableRefObject<EditQuizAssessmentUUids | undefined>;
	enforcedProfilesOnly: boolean;
	existingReferenceMaterialFiles?: CoreFileItem[];
	/**
	 * The reference material a caller already holds, for the same reason `initialLiveSessions` exists: the
	 * wizard's loader needs a quiz id to resolve an assessment folder, and a caller whose files live
	 * outside one has none. Seeded where the fetch would have landed; callers that omit it are untouched.
	 */
	initialReferenceMaterialFiles?: CoreFileItem[];

	onBackEnabledChange?: (changes: EditQuizButtonState) => void;
	onNextEnabledChange?: (changes: EditQuizButtonState) => void;
	/** Called when the proctoring options can or cannot be saved */
	onSaveEnabledChange?: (changes: EditQuizButtonState) => void;
	onSaveProfileChange?: (changes: EditQuizButtonState) => void;
	setIsFirstPage?: (value: boolean) => void;
	setIsFinalPage?: (value: boolean) => void;
	forceStartOnFirstPage?: boolean;

	/** Called right before the options are being saved */
	onStartSaveOptions?: (confirmedOptions?: InitialProctoringState) => void;
	/** Called when options have finished saving (`success = true`)
	 * or failed to save (`success = false`). A `message` may be set if `success` is `false`. */
	onSaveOptions: (
		success: boolean,
		messages?: string[],
		options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0],
		result?: OnSaveOptionsResults,
	) => void;
	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>;
	setUploadedProfilesMap: React.Dispatch<React.SetStateAction<Map<number, ProctoringOptionProfileV2>>>;
	logger?: VerityLogger;

	onClose?: (confirmedOption?: InitialProctoringState) => void;

	defaultProctoringOption?: ProctoringOption | null;

	/* Wraps the child component (stepper) in a predefined component */
	step?: number;
	setStep?: Dispatch<SetStateAction<number>>;

	/**@default "lms" */
	quizType?: EditQuizType;
	/**@default "lms-modal" */
	entryPoint?: EditQuizEntryPoint;

	verityOrigin?: string;

	checkThemeExists?: () => Promise<boolean>;

	/**Called whenever the initial settings fetch (including reference material files) starts/stops loading. */
	onSettingsLoadingChange?: (loading: boolean) => void;

	/**Hides the accommodations block on the confirmation step, for surfaces with no roster to manage. */
	hideAccommodations?: boolean;
};

export type ExemptionSectionInfo = Pick<
	ExemptionSectionProps,
	| "exemptionInfo"
	| "profilesMap"
	| "canDeleteProfile"
	| "onDelete"
	| "onRegisterSave"
	| "onRosterChange"
	| "view"
	| "onInvalidDraft"
> & {
	onViewChange: (view?: ExemptionSectionView) => void;
	numExempted: number;
};

export type EditQuizRef = {
	saveLiveSessionHandler: (quizId?: string) => Promise<void>;
	saveButtonHandler: (password?: string, additionalAssessmentInfo?: Partial<AssessmentInfo>) => Promise<boolean>;
	closeButtonHandler: () => Promise<boolean>;
	saveAsProfileButtonHandler: () => void;
	nextButtonHandler: () => void;
	backButtonHandler: () => void;
	goToPage: (pageName: EditQuizStep) => void;
	getStepKeyByIndex: (index: number) => EditQuizStep;
	getStepConfig: () => StepConfig<EditQuizStep>[];

	// Exposed for verity assessment workflow because the confirmation step is rendered outside edit quiz
	getActiveProfile: () => ProctoringOptionProfileV2 | undefined;

	// Since accommodation is now combined with confirmation, workflows that render confirmation outside of edit quiz need this state
	getExemptionSectionInfo: () => ExemptionSectionInfo;

	getReferenceMaterialFiles?: () => CoreFileItem[] | undefined;
	setReferenceMaterialFiles?: (files: CoreFileItem[]) => void;
	saveReferenceMaterials?: (quizId?: string, files?: CoreFileItem[]) => Promise<void>;
	/**Validates reference material settings, showing a toast if invalid (unless showToast is false). Returns true if valid. */
	validateReferenceMaterialSetting?: (options?: { showToast?: boolean }) => boolean;

	/**
	 * Marks the step the user is currently on as completed. Needed by callers (e.g. an outer wizard's own
	 * Next button) that leave edit quiz's last page by some means other than nextButtonHandler, since that's
	 * normally what marks the step being left as completed.
	 */
	markCurrentStepCompleted: () => void;

	getThirdPartyPlatforms: () => ThirdPartyPlatform[];
};

export const EditQuizPage = forwardRef(function EditQuizPage(
	{
		api = defaultApi,
		forceStartOnFirstPage = false,
		quizType = "lms",
		entryPoint = "lms-modal",
		...props
	}: EditQuizPageProps,
	ref: ForwardedRef<EditQuizRef>,
) {
	// Moved init logic to respective hooks for organization
	const {
		live: liveConfig,
		nav: navConfig,
		profile: profileConfig,
		submit: submitConfig,
		setting: settingConfig,
		student: studentConfig,
		theme: themeConfig,
	} = useEditQuizState({
		...props,
		api,
		forceStartOnFirstPage,
		quizType,
		entryPoint,
	});

	const {
		enforcedProfilesOnly,
		onSaveOptions,
		quizInfo,
		setCurrentProfileId,
		setProctoringAssessmentConfiguration,
		setProctoringOption,
		initialProctoringAssessmentConfiguration,
		logger,
		onBackEnabledChange,
		onNextEnabledChange,
		onSaveEnabledChange,
		onStartSaveOptions,
		proctoringAssessmentConfiguration,
		proctoringOption,
		setIsFinalPage,
		url,
		onSaveProfileChange,
		onClose,
		currentProfileId,
		setUploadedProfilesMap,
		setIsFirstPage,
		verityOrigin,
	} = props;
	const lmsSchedule = getLmsScheduleInfo(quizInfo);

	const navHook = useEditQuizNavigation({
		enforcedProfilesOnly,
		live: liveConfig,
		nav: navConfig,
		profile: profileConfig,
		setProctoringAssessmentConfiguration,
		setting: settingConfig,
		onBackEnabledChange,
		onNextEnabledChange,
		proctoringAssessmentConfiguration,
		setIsFinalPage,
		setIsFirstPage,
		quizType,
		entryPoint,
		student: studentConfig,
		proctoringOption,
		quizInfo,
		setProctoringOption,
	});
	const {
		curPageName,
		handleBackButton,
		handleGoToStep,
		handleNextButton,
		handleStepChange,
		getStepKeyByIndex,
		completedStepIndexes,
		stepConfig,
		handleConfirmLiveWarning,
		setupGuideState,
		markCurrentStepCompleted,
	} = navHook;

	useEditQuizInit({
		enforcedProfilesOnly,
		live: liveConfig,
		profile: profileConfig,
		setProctoringAssessmentConfiguration,
		setting: settingConfig,
		student: studentConfig,
		theme: themeConfig,
		proctoringAssessmentConfiguration,
		url,
	});

	const { saveButtonHandler, closeButtonHandler, saveLiveSessionHandler } = useEditQuizSubmit({
		api,
		enforcedProfilesOnly,
		live: liveConfig,
		onSaveOptions,
		profile: profileConfig,
		quizInfo,
		submit: submitConfig,
		setCurrentProfileId,
		setting: settingConfig,
		student: studentConfig,
		theme: themeConfig,
		logger,
		onSaveEnabledChange,
		onStartSaveOptions,
		proctoringAssessmentConfiguration,
		proctoringOption,
		onClose,
		quizType,
		currentProfileId,
	});

	const { handleDeleteProfile, handleSaveProfile, handleSelectProfile, handleUpdateProfile } = useEditQuizProfile({
		profile: profileConfig,
		onSaveProfileChange,
		setting: settingConfig,
		quizInfo,
		quizType,
		setProctoringOption,
		currentProfileId,
		proctoringAssessmentConfiguration,
		proctoringOption,
		setUploadedProfilesMap,
		student: studentConfig,
	});

	// Todo: to fix future accommodation bugs, should extract useStudentState out and have consumers pass it in
	const handleGetExemptionSectionInfo: EditQuizRef["getExemptionSectionInfo"] = useCallback(() => {
		// Read the ref rather than studentConfig.state.exemptionInfo: this can be called (via editQuizRef)
		// synchronously right after updateRoster fires, before React has committed that state update, and
		// the ref is the only value guaranteed to reflect the just-applied roster change at that point.
		const currentExemptionInfo = studentConfig.state.exemptionInfoRef.current;

		return {
			canDeleteProfile: profileConfig.action.isDeleteProfileAllowed,
			exemptionInfo: currentExemptionInfo,
			onDelete: handleDeleteProfile,
			onRosterChange: studentConfig.action.updateRoster,
			onViewChange: studentConfig.action.updateAccommodationView,
			onRegisterSave: studentConfig.action.registerAccommodationSaveFn,
			profilesMap: profileConfig.state.profileMap,
			onInvalidDraft: studentConfig.action.updateDraftInvalid,
			view: studentConfig.state.accommodationView,
			numExempted: currentExemptionInfo.exemptRoster.size,
		};
	}, [
		handleDeleteProfile,
		profileConfig.action.isDeleteProfileAllowed,
		profileConfig.state.profileMap,
		studentConfig.action.registerAccommodationSaveFn,
		studentConfig.action.updateAccommodationView,
		studentConfig.action.updateDraftInvalid,
		studentConfig.action.updateRoster,
		studentConfig.state.accommodationView,
		studentConfig.state.exemptionInfoRef,
	]);

	useImperativeHandle(
		ref,
		() => ({
			saveLiveSessionHandler,
			saveButtonHandler,
			closeButtonHandler,
			saveAsProfileButtonHandler: profileConfig.action.saveAsProfileButtonHandler,
			nextButtonHandler: handleNextButton,
			backButtonHandler: handleBackButton,
			goToPage: handleGoToStep,
			getStepConfig: () => stepConfig,
			getStepKeyByIndex,
			getActiveProfile: () => profileConfig.state.activeProfile,
			getExemptionSectionInfo: handleGetExemptionSectionInfo,
			getReferenceMaterialFiles: () => settingConfig.state.referenceMaterialFiles,
			setReferenceMaterialFiles: (files: CoreFileItem[]) => settingConfig.action.setReferenceMaterialFiles(files),
			saveReferenceMaterials: settingConfig.action.saveReferenceMaterials,
			validateReferenceMaterialSetting: settingConfig.action.validateReferenceMaterialSetting,
			markCurrentStepCompleted,
			getThirdPartyPlatforms: () => settingConfig.state.proctoringData.thirdPartyPlatforms,
		}),
		[
			saveLiveSessionHandler,
			saveButtonHandler,
			markCurrentStepCompleted,
			closeButtonHandler,
			profileConfig.action.saveAsProfileButtonHandler,
			profileConfig.state.activeProfile,
			handleNextButton,
			handleBackButton,
			handleGoToStep,
			getStepKeyByIndex,
			handleGetExemptionSectionInfo,
			settingConfig.action,
			settingConfig.state.referenceMaterialFiles,
			settingConfig.state.proctoringData.thirdPartyPlatforms,
			stepConfig,
		],
	);

	// Destructuring common props
	const { verityInstituteSettings, optionRelatedInstituteSettings, loading, enabledProctoringOptions, proctoringData } =
		settingConfig.state;

	if (!proctoringAssessmentConfiguration || !proctoringOption || !verityInstituteSettings) return null;

	return (
		<VerityApiContext.Provider value={api}>
			<I18nextProvider i18n={i18nPackage as i18n}>
				<form
					aria-label={i18nPackage.t("editQuizPage.formAriaLabel")}
					id="yuja-options-form"
					name="yuja-options-form"
					className={styles.editQuizContainer}
				>
					<div className={styles.stepperContainer}>
						<EditQuizStepper
							activeStep={navConfig.state.curPageValue}
							steps={stepConfig}
							onStepChange={handleStepChange}
							completedSteps={completedStepIndexes}
							loading={loading}
							highlightText={isProctoringOptionCategory(curPageName) ? setupGuideState?.description : undefined}
							setupGuideNextEnabled={setupGuideState?.isNextEnabled}
						/>
					</div>

					<div tabIndex={-1} ref={navConfig.state.modalBodyRef} className={styles.modalBody}>
						{proctoringOption.isBrowser &&
							!isBrowserPlatformOffered({
								quizType,
								allowBrowserPlatform: verityInstituteSettings.allowBrowserPlatform,
							}) && (
								<Alert
									severity="warning"
									action={
										<Button
											onClick={() => handleGoToStep(profileConfig.state.canShowProfileStep ? "profile" : "platform")}
										>
											{i18nPackage.t("editQuizPage.chooseCompatiblePlatform")}
										</Button>
									}
								>
									{i18nPackage.t("editQuizPage.incompatibleBrowserProfile")}
								</Alert>
							)}
						{proctoringAssessmentConfiguration.thirdPartyAssessmentOption && curPageName === "thirdParty" && (
							<ThirdPartyStep
								isPortalMadeThirdPartyAssessment={quizType === "third-party"}
								isFirstTime={!initialProctoringAssessmentConfiguration?.enabled}
								thirdPartyPassword={proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.password}
								url={proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyUrl ?? undefined}
								setThirdPartyUrl={(url) =>
									setProctoringAssessmentConfiguration({
										...proctoringAssessmentConfiguration,
										thirdPartyAssessmentOption: {
											...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
											thirdPartyUrl: url,
										},
									})
								}
								setThirdPartyPassword={(password) =>
									setProctoringAssessmentConfiguration({
										...proctoringAssessmentConfiguration,
										thirdPartyAssessmentOption: {
											...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
											password: password,
										},
									})
								}
								selectedPlatformId={proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyPlatformId}
								setSelectedPlatformId={(platformId) =>
									setProctoringAssessmentConfiguration({
										...proctoringAssessmentConfiguration,
										thirdPartyAssessmentOption: {
											...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
											thirdPartyPlatformId: platformId,
										},
									})
								}
								platforms={proctoringData.thirdPartyPlatforms}
								proctoringOption={proctoringOption}
								setProctoringOption={setProctoringOption}
								loading={loading}
							/>
						)}

						{curPageName === "platform" && (
							<PlatformStep
								forceChromeOsManagedGuestSession={proctoringAssessmentConfiguration.forceChromeOsManagedGuestSession}
								loading={loading}
								quizType={quizType}
								enforcedProfilesOnly={enforcedProfilesOnly}
								proctoringOption={proctoringOption}
								setProctoringOption={setProctoringOption}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								setupGuideState={setupGuideState}
							/>
						)}
						{curPageName === "profile" && (
							<ProfileStep
								profiles={profileConfig.state.profiles}
								proctoringData={proctoringData}
								activeProfile={profileConfig.state.activeProfile}
								onProfileSelect={handleSelectProfile}
								enforcedProfilesOnly={enforcedProfilesOnly}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								loading={loading}
								showPresetProfile={profileConfig.state.showPresetGridUI}
								canDeleteProfile={profileConfig.action.isDeleteProfileAllowed}
								onDelete={handleDeleteProfile}
								setupGuideState={setupGuideState}
								currentProfileId={currentProfileId}
								isCustomConfig={profileConfig.state.isCustomConfig}
								lms={quizInfo.lms}
							/>
						)}

						{isProctoringOptionCategory(curPageName) && (
							<ConfigurationStep
								loading={loading}
								data={proctoringData}
								currentCategory={curPageName}
								enabledProctoringOptions={enabledProctoringOptions}
								enforcedProfilesOnly={enforcedProfilesOnly}
								proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
								proctoringOption={proctoringOption}
								setProctoringOption={setProctoringOption}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								quizType={quizType}
								referenceMaterialFiles={settingConfig.state.referenceMaterialFiles}
								onUpdateReferenceMaterialFiles={settingConfig.action.setReferenceMaterialFiles}
								api={api}
							/>
						)}

						{curPageName === "method" && (
							<MethodStep
								enabledProctoringOptions={enabledProctoringOptions}
								updateOptions={setProctoringAssessmentConfiguration}
								hasUpcomingSessions={() => {
									const currTime = new Date();
									return Object.values(liveConfig.state.pendingLiveSessions).some(
										({ startTime }) => startTime > currTime,
									);
								}}
								unmarkStepAsCompleted={navConfig.action.unmarkStepAsCompleted}
								options={proctoringAssessmentConfiguration}
								verityInstituteSettings={verityInstituteSettings}
								loading={loading}
								setupGuideState={setupGuideState}
							/>
						)}

						{curPageName === "liveSessions" && (
							<LiveSessionsStep
								existingLiveSessions={liveConfig.state.existingLiveSessions}
								pendingLiveSessionIds={liveConfig.state.pendingLiveSessionIds}
								pendingLiveSessions={liveConfig.state.pendingLiveSessions}
								syncStartedSessions={liveConfig.action.syncStartedSessions}
								availableProctorGroups={liveConfig.state.availableProctorGroups}
								availableProctors={liveConfig.state.availableProctors}
								liveSessionsNextConfirmationMessage={liveConfig.state.liveSessionsNextConfirmationMessage}
								cancelNextConfirmationDialog={liveConfig.action.resetConfirmationMessage}
								confirmNextConfirmationDialog={handleConfirmLiveWarning}
								allowDemoSessions={settingConfig.state.accountType === "demo"}
								lmsSchedule={lmsSchedule}
								addPendingSession={liveConfig.action.addPendingSession}
								removePendingSession={liveConfig.action.removePendingSession}
								updatePendingSession={liveConfig.action.updatePendingSession}
								verityOrigin={verityOrigin}
							/>
						)}

						{curPageName === "confirmation" && (
							<ConfirmationPage
								pendingLiveSessions={liveConfig.state.pendingLiveSessionsArray}
								loading={loading}
								handleGoToStep={handleGoToStep}
								enforcedProfilesOnly={enforcedProfilesOnly}
								proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
								proctoringOption={proctoringOption}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								activeProfile={profileConfig.state.activeProfile}
								applications={proctoringData.applications}
								extensions={proctoringData.extensions}
								thirdPartyPlatforms={proctoringData.thirdPartyPlatforms}
								quizName={props.quizName}
								referenceMaterialFiles={settingConfig.state.referenceMaterialFiles}
								allowedCategories={settingConfig.state.allowedCategories}
								exemptionInfo={studentConfig.state.exemptionInfo}
								profilesMap={profileConfig.state.profileMap}
								canDeleteProfile={profileConfig.action.isDeleteProfileAllowed}
								onDelete={handleDeleteProfile}
								onInvalidDraft={studentConfig.action.updateDraftInvalid}
								onViewChange={studentConfig.action.updateAccommodationView}
								onRegisterSave={studentConfig.action.registerAccommodationSaveFn}
								onRosterChange={studentConfig.action.updateRoster}
								accommodationView={studentConfig.state.accommodationView}
								numAccommodated={studentConfig.state.numExempted}
								hideAccommodations={props.hideAccommodations}
								lms={quizInfo.lms}
							/>
						)}

						{quizType !== "standalone" &&
							!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption &&
							curPageName === "confirmation" &&
							submitConfig.state.disclaimerModalOpen && (
								<DisclaimerModal
									verifyingTheme={themeConfig.state.verifyingTheme}
									onClose={submitConfig.action.closeDisclaimerModal}
									onSubmit={submitConfig.action.submitDisclaimerModal}
								/>
							)}

						{profileConfig.state.saveProfileModalShowing && (
							<SaveProfile
								activeProfile={profileConfig.state.activeProfile}
								onClose={() => profileConfig.action.hideSaveProfileModal()}
								hasInstituteProfilePermissions={profileConfig.state.hasInstituteProfilePermissions}
								onSaveNewProfile={handleSaveProfile}
								onUpdateProfile={handleUpdateProfile}
								canPublishToCourse={Boolean(quizInfo.courseId)}
								isThirdPartyEnabled={Boolean(proctoringAssessmentConfiguration.thirdPartyAssessmentOption)}
								lms={quizInfo.lms}
							/>
						)}

						{submitConfig.state.unsavedChangesModalOpen && (
							<UnsavedChangesModal
								onClose={submitConfig.action.closeUnsavedChangesModal}
								onConfirm={submitConfig.action.confirmUnsavedChangesModal}
							/>
						)}

						{submitConfig.state.noConfigurationsEnabledModalOpen && (
							<NoConfigurationsEnabledModal
								onClose={submitConfig.action.closeNoConfigModal}
								onConfirm={submitConfig.action.confirmNoConfigModal}
							/>
						)}
					</div>
				</form>
			</I18nextProvider>
		</VerityApiContext.Provider>
	);
});
